import { useNavigate } from 'react-router-dom'

import Button from '@/components/Button'
import Tooltip from '@/components/Tooltip'
import { ServiceItemInterface } from '@/lib/definitions/services'
import { cn } from '@/lib/misc/Utils'
import ArrowTopRightOnSquareIcon from '@icons/arrow-top-right-on-square'
import BookOpenIcon from '@icons/book-open'
import PlusIcon from '@icons/plus'
import { useKnowledgeBaseLink } from '@/lib/hooks/useKnowledgeBaseLink'

const CLAMP_CLASS: Record<number, string> = {
  3: 'line-clamp-3',
  4: 'line-clamp-4',
}

interface ProductCardProps {
  product: ServiceItemInterface
  cy?: string
  clampLines?: 3 | 4
  onSelect?: (product: ServiceItemInterface) => void
  actions?: 'footer' | 'header-link'
}

const ProductCard = ({
  product,
  cy,
  clampLines,
  onSelect,
  actions = 'footer',
}: ProductCardProps) => {
  const knowledgeBaseLink = useKnowledgeBaseLink()
  const productInfoLink = product.knowledgeBaseUrl
    ? knowledgeBaseLink(product.knowledgeBaseUrl)
    : undefined
  const navigate = useNavigate()

  const handleAdd = () => {
    onSelect?.(product)
    if (product.cta) navigate(product.cta)
  }

  return (
    <div
      data-cy={cy}
      className={cn(
        'relative flex flex-col p-3 rounded-lg bg-neutral-10/50',
        'transition-all duration-150 group overflow-hidden',
        onSelect &&
          'cursor-pointer outline outline-2 outline-transparent hover:outline-text-link'
      )}
      onClick={onSelect ? handleAdd : undefined}
    >
      <div className="flex items-center gap-2.5">
        <div className="size-9 flex items-center justify-center rounded-lg bg-neutral-05 border border-neutral-10 text-accent-100 shrink-0 group-hover:animate-[swing_300ms_ease-in-out]">
          {product.icon}
        </div>

        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="min-w-0 text-neutral-90 font-medium leading-tight">
            {product.title}
          </p>

          {product.isNew && (
            <span className="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide bg-green-200 text-status-good-900 motion-safe:animate-badge-in">
              New
            </span>
          )}
        </div>

        {actions === 'header-link' && productInfoLink && (
          <Tooltip label="Knowledge Base" inline>
            <Button
              as="link"
              style="flat"
              href={productInfoLink.href}
              target={productInfoLink.target}
              className="shrink-0 py-1 px-2 bg-neutral-10 hover:bg-neutral-20"
              onClick={(e) => {
                e.stopPropagation()
                productInfoLink.onClick(e)
              }}
            >
              <BookOpenIcon className="size-4 text-neutral-90" />
            </Button>
          </Tooltip>
        )}
      </div>

      <p
        className={cn(
          'mt-2 flex-1 text-sm text-neutral-50 leading-snug',
          clampLines && CLAMP_CLASS[clampLines]
        )}
      >
        {product.description}
      </p>

      {actions === 'footer' && (
        <div className="mt-3 pt-3 flex flex-wrap items-center justify-between gap-2 border-t border-hairline">
          {product.cta && (
            <Button
              style="secondary"
              size="sm"
              className="border-none bg-neutral-10 text-sm"
              onClick={handleAdd}
              icon={<PlusIcon className="size-3 transition-transform" />}
            >
              Add
            </Button>
          )}
          {productInfoLink && (
            <Button
              as="link"
              href={productInfoLink.href}
              target={productInfoLink.target}
              onClick={(e) => {
                e.stopPropagation()
                productInfoLink.onClick(e)
              }}
              style="secondary"
              size="sm"
              className="border-none bg-neutral-20 text-sm"
              iconSuffix={
                <ArrowTopRightOnSquareIcon className="size-3 transition-transform" />
              }
            >
              <span>Product Info</span>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default ProductCard
